import { tr } from '@/lib/i18n-core';
import { MessagesButton } from './flow/MessagesDrawer';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PRE_AUTH_TABS, type BillFromAuthorisationDto, type IntegrationCallOutcomeDto, type PreAuthDiscardResultDto, type PreAuthRequiredRowDto, type PreAuthTab, type PreAuthTabCountsDto } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { visibleInterval } from '@/lib/polling';
import { dateOnly, humanize, money } from '@/lib/utils';
import { ConfirmDialog, DataTable, Dialog, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, SkeletonText, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ExternalSendButton } from '../integrations/ExternalSendButton';
import { NphiesStatusLabel } from '../integrations/ExternalStatusBadge';
import { describeIntegrationError, reportOutcome } from '../integrations/external-outcome';
import { AdjudicationBadge, ExchangeBadge, PRE_AUTH_KIND_LABEL } from './insurance-status';
import { ManualPreAuthWorklist } from './ManualPreAuthWorklist';
import { PreAuthSendCheck } from './PreAuthReadiness';
import { insuranceTabPath, withReturnTo } from './insurance-routes';
import { nphiesActionsFor } from './authorisation';

interface PreAuthRow {
  id: string;
  status: string;
  kind: string;
  exchangeState: string;
  exchangeNote: string | null;
  preauthNo: string | null;
  diagnosis: string | null;
  procedure: string | null;
  notes: string | null;
  requestedAmountMinor: number;
  approvedAmountMinor: number;
  patientShareMinor: number;
  requestedAt: string;
  authFrom: string | null;
  authTo: string | null;
  admissionId: string | null;
  lineCount: number;
  payer: { id: string; name: string; onNphies?: boolean };
  patient: { id: string; mrn: string; fullName: string } | null;
}

interface PreAuthDetail extends Omit<PreAuthRow, 'lineCount' | 'payer'> {
  payer: { id: string; name: string };
  lines: {
    id: string;
    sequence: number;
    code: string;
    display: string;
    quantity: number;
    approvedQuantity: number | null;
    submittedMinor: number;
    approvedMinor: number;
    patientShareMinor: number;
    taxMinor: number;
    adjudicationStatus: string;
    reasonCode: string | null;
    reasonText: string | null;
    overrideReason: string | null;
  }[];
}

const TAB_LABEL: Record<PreAuthTab, string> = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', failed: 'Failed', archived: 'Archived' };
const TAB_EMPTY: Record<PreAuthTab, string> = {
  pending: 'Nothing is waiting for the insurer.',
  approved: 'No approved pre-authorisations.',
  rejected: 'Nothing was rejected.',
  failed: 'Nothing failed to send.',
  archived: 'Nothing archived.',
};

function PreAuthDetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const detail = useQuery({ queryKey: ['pre-auth-detail', id], queryFn: () => get<PreAuthDetail>(`/insurance/pre-auths/${id}`) });
  const row = detail.data;
  return (
    <Dialog open onClose={onClose} wide title="Pre-authorisation" description={row ? `${row.patient?.fullName ?? ''} · ${row.payer.name}` : undefined} footer={<Button variant="ghost" onClick={onClose}>Close</Button>}>
      {detail.isLoading && <SkeletonText lines={2} />}
      {detail.error && <Alert variant="destructive">{describeError(detail.error)}</Alert>}
      {row && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <StatusBadge status={row.status} />
            <ExchangeBadge state={row.exchangeState} />
            <Badge variant="secondary">{PRE_AUTH_KIND_LABEL[row.kind] ?? humanize(row.kind)}</Badge>
            {row.preauthNo && <span>Approval number <span className="font-mono">{row.preauthNo}</span></span>}
            {row.authFrom && row.authTo && <span className="text-muted-foreground">{dateOnly(row.authFrom)} – {dateOnly(row.authTo)}</span>}
          </div>
          {row.exchangeNote && <Alert variant={row.exchangeState === 'failed' ? 'destructive' : 'warning'}>{row.exchangeNote}</Alert>}
          {row.notes && (
            <Alert>
              <span className="font-medium">Note from the insurer: </span>
              {row.notes}
            </Alert>
          )}
          <DataTable
            dense
            rows={row.lines}
            rowKey={(line) => line.id}
            empty="This request carries no lines yet. They are added when it is sent."
            columns={[
              { header: '#', width: '40px', cell: (line) => line.sequence },
              { header: 'Service', cell: (line) => <div><p>{line.display}</p><p className="font-mono text-xs text-muted-foreground">{line.code}</p></div> },
              { header: 'Qty', align: 'right', cell: (line) => (line.approvedQuantity !== null && line.approvedQuantity !== line.quantity ? `${line.approvedQuantity} of ${line.quantity}` : line.quantity) },
              { header: 'Asked', align: 'right', cell: (line) => money(line.submittedMinor) },
              { header: 'Insurer pays', align: 'right', cell: (line) => money(line.approvedMinor) },
              { header: 'Patient pays', align: 'right', cell: (line) => money(line.patientShareMinor) },
              { header: 'Answer', cell: (line) => <AdjudicationBadge status={line.adjudicationStatus} /> },
              {
                header: 'Why',
                cell: (line) => (
                  <span className="text-xs text-muted-foreground">
                    {line.reasonCode && <span className="me-1 font-mono">{line.reasonCode}</span>}
                    {line.reasonText}
                    {line.overrideReason && <span className="block text-warning">Sent again: {line.overrideReason}</span>}
                  </span>
                ),
              },
            ]}
          />
        </div>
      )}
    </Dialog>
  );
}

function isDraft(row: { status: string; exchangeState: string }): boolean {
  return row.status === 'requested' && (row.exchangeState === 'unsent' || row.exchangeState === 'failed');
}

function NeededWorklist({ onRequest }: { onRequest: (row: PreAuthRequiredRowDto) => void }) {
  const needed = useQuery({
    queryKey: ['pre-auth-needed'],
    queryFn: () => get<{ items: PreAuthRequiredRowDto[]; nextCursor: string | null }>('/insurance/pre-auths/needed?limit=20'),
    refetchInterval: visibleInterval(120_000),
  });
  const rows = needed.data?.items ?? [];
  if (rows.length === 0) return null;
  return (
    <Card className="mb-4 border-warning/40">
      <CardHeader>
        <CardTitle className="text-sm">Admitted patients whose insurer requires a pre-authorisation that is not in place</CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          dense
          rows={rows}
          rowKey={(row) => row.admissionId}
          columns={[
            { header: 'Admission', sort: (row) => row.admissionNumber, cell: (row) => row.admissionNumber },
            { header: 'Patient', sort: (row) => row.patientName, cell: (row) => row.patientName },
            { header: 'Payer', sort: (row) => row.payerName, cell: (row) => row.payerName },
            { header: 'Admitted', sort: (row) => row.admittedAt, cell: (row) => dateOnly(row.admittedAt) },
            { header: '', align: 'right', cell: (row) => <Button size="sm" variant="outline" onClick={() => onRequest(row)}>Request one</Button> },
          ]}
        />
        <p className="mt-2 text-xs text-muted-foreground">The claim cannot be submitted without the approval number. Raise the request now so it is not the discharge that waits.</p>
      </CardContent>
    </Card>
  );
}

function useOpenNewPreAuth() {
  const navigate = useNavigate();
  const returnTo = insuranceTabPath('preauth');
  return (kind: 'manual' | 'request', initial: { payerId?: string; admissionId?: string } = {}) =>
    navigate(withReturnTo(`/insurance/pre-auths/new?kind=${kind}${qs(initial).replace(/^\?/, '&')}`, returnTo));
}

export function PreAuthActions() {
  const { can, hasModule } = useAuth();
  const navigate = useNavigate();
  const nphies = hasModule('nphies');
  const openNew = useOpenNewPreAuth();
  return (
    <>
      {can('preauth.manage') && <Button variant="outline" onClick={() => navigate(withReturnTo('/insurance/pre-auths/extend', insuranceTabPath('preauth')))}>Extend a stay</Button>}
      {can('preauth.manage') && <Button variant={nphies ? 'outline' : 'default'} onClick={() => openNew('manual')}>{nphies ? 'Record an approval given outside NPHIES' : 'Record a pre-authorisation'}</Button>}
      {can('nphies.preauth.send') && nphies && <Button onClick={() => openNew('request')}>New request</Button>}
    </>
  );
}

export function PreAuthPanel() {
  const { can, hasModule } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const nphies = hasModule('nphies');
  const [tab, setTab] = useState<PreAuthTab>('pending');
  const [viewing, setViewing] = useState<string | null>(null);
  const [discarding, setDiscarding] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [discardingSelected, setDiscardingSelected] = useState(false);
  const returnTo = insuranceTabPath('preauth');
  const openNew = useOpenNewPreAuth();

  const counts = useQuery({
    queryKey: ['pre-auth-counts'],
    queryFn: () => get<PreAuthTabCountsDto>('/insurance/pre-auths/counts'),
    refetchInterval: visibleInterval(60_000),
  });

  const list = useInfiniteQuery({
    queryKey: ['pre-auth-tab', tab],
    initialPageParam: '',
    queryFn: ({ pageParam }) => get<{ items: PreAuthRow[]; nextCursor: string | null }>(`/insurance/pre-auths/list${qs({ tab, limit: 30, cursor: pageParam || undefined })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((page) => page.items);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['pre-auth-tab'] });
    void queryClient.invalidateQueries({ queryKey: ['pre-auth-counts'] });
    void queryClient.invalidateQueries({ queryKey: ['pre-auth-detail'] });
    void queryClient.invalidateQueries({ queryKey: ['pre-auth-needed'] });
  };

  const discard = useMutation({
    mutationFn: (ids: string[]) => post<PreAuthDiscardResultDto>('/insurance/pre-auths/discard', { ids }),
    onSuccess: (result) => {
      toast.success(`Discarded ${result.discarded.length}`, result.skipped.length > 0 ? `${result.skipped.length} could not be discarded: ${result.skipped[0]?.reason}` : undefined);
      setSelected([]);
      refresh();
    },
    onError: (error) => toast.error('Could not discard', describeError(error)),
  });

  const bill = useMutation({
    mutationFn: (id: string) => post<BillFromAuthorisationDto>(`/insurance/pre-auths/${id}/bill`, {}),
    onSuccess: (result) => {
      toast.success(`Invoice ${result.invoiceNumber} raised for ${result.billedLines} approved service${result.billedLines === 1 ? '' : 's'}`, result.skipped.length > 0 ? `${result.skipped.length} not billed: ${result.skipped[0]?.reason}` : undefined);
      refresh();
    },
    onError: (error) => toast.error('Could not bill it', describeError(error)),
  });

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'cancel' | 'status-check' | 'archive' }) =>
      action === 'archive' ? post(`/insurance/pre-auths/${id}/archive`, {}) : post<IntegrationCallOutcomeDto>(`/integrations/nphies/pre-auths/${id}/${action}`, {}),
    onSuccess: (outcome, variables) => {
      if (variables.action === 'archive') toast.success('Archived');
      else reportOutcome(toast, outcome as IntegrationCallOutcomeDto, variables.action === 'cancel' ? 'Cancellation sent' : 'The insurer answered');
      refresh();
    },
    onError: (error) => toast.error('Could not do that', describeIntegrationError(error)),
  });

  return (
    <>
      <NeededWorklist onRequest={(row) => (nphies ? openNew('request', { payerId: row.payerId, admissionId: row.admissionId }) : openNew('manual'))} />
      {can('preauth.manage') && <ManualPreAuthWorklist onOpenDraft={(id) => setViewing(id)} />}

      <Tabs
        active={tab}
        onChange={(id) => {
          setTab(id as PreAuthTab);
          setSelected([]);
        }}
        tabs={PRE_AUTH_TABS.map((id) => ({
          id,
          label: TAB_LABEL[id],
          badge: counts.data ? <Badge variant={id === 'failed' && counts.data[id] > 0 ? 'destructive' : 'secondary'}>{counts.data[id]}</Badge> : undefined,
        }))}
      />

      <DataTable
        rows={rows}
        loading={list.isLoading} refreshing={list.isFetching}
        error={list.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => setViewing(row.id)}
        empty={TAB_EMPTY[tab]}
        footer={
          (selected.length > 0 && can('preauth.manage')) || list.hasNextPage ? (
            <>
              {selected.length > 0 && can('preauth.manage') ? (
                <div className="flex items-center gap-3">
                  <span>{selected.length} selected</span>
                  <Button
                    size="sm"
                    variant="outline"
                    loading={discard.isPending}
                    onClick={() => setDiscardingSelected(true)}
                  >
                    Discard selected
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelected([])}>Clear</Button>
                </div>
              ) : (
                <span />
              )}
              {list.hasNextPage && (
                <Button variant="outline" size="sm" onClick={() => void list.fetchNextPage()} loading={list.isFetchingNextPage}>Show more</Button>
              )}
            </>
          ) : undefined
        }
        columns={[
          {
            header: '',
            width: '32px',
            cell: (row) =>
              isDraft(row) && can('preauth.manage') ? (
                <Checkbox
                  aria-label="Select this request"
                  checked={selected.includes(row.id)}
                  onClick={(event) => event.stopPropagation()}
                  onCheckedChange={(checked) => setSelected(checked ? [...selected, row.id] : selected.filter((id) => id !== row.id))}
                />
              ) : null,
          },
          { header: 'Raised', sort: (row) => row.requestedAt, cell: (row) => dateOnly(row.requestedAt) },
          { header: 'Patient', sort: (row) => row.patient?.fullName, cell: (row) => (row.patient ? <div><p>{row.patient.fullName}</p><p className="text-xs text-muted-foreground">{row.patient.mrn}</p></div> : '—') },
          { header: 'Payer', sort: (row) => row.payer.name, cell: (row) => row.payer.name },
          { header: 'Request', cell: (row) => <div><p className="max-w-xs truncate">{row.procedure ?? row.diagnosis ?? '—'}</p><p className="text-xs text-muted-foreground">{PRE_AUTH_KIND_LABEL[row.kind] ?? row.kind} · {row.lineCount} line{row.lineCount === 1 ? '' : 's'}</p></div> },
          { header: 'Window', sort: (row) => row.authFrom, cell: (row) => (row.authFrom && row.authTo ? <span className="whitespace-nowrap text-xs">{dateOnly(row.authFrom)} – {dateOnly(row.authTo)}</span> : '—') },
          { header: 'Asked', align: 'right', sort: (row) => row.requestedAmountMinor, cell: (row) => money(row.requestedAmountMinor) },
          { header: 'Approved', align: 'right', sort: (row) => row.approvedAmountMinor, cell: (row) => money(row.approvedAmountMinor) },
          { header: 'Number', sort: (row) => row.preauthNo, cell: (row) => row.preauthNo ?? '—' },
          {
            header: 'Status',
            sort: (row) => row.status,
            cell: (row) => (
              <span className="flex flex-col gap-1" title={row.exchangeNote ?? undefined}>
                <StatusBadge status={row.status} />
                <ExchangeBadge state={row.exchangeState} />
              </span>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <span className="flex flex-wrap items-center justify-end gap-2" onClick={(event) => event.stopPropagation()}>
                <MessagesButton entityType="pre_authorization" entityId={row.id} title={tr('billing', 'nx_messages_for_preauth', { number: row.preauthNo ?? row.id.slice(0, 8) })} />
                <NphiesStatusLabel last={(row as { lastMessage?: { status: string; errorMessage: string | null; createdAt: string } | null }).lastMessage} />
                {row.status === 'requested' && ['unsent', 'failed'].includes(row.exchangeState) && nphiesActionsFor(row, nphies) && (
                  <ExternalSendButton
                    path={`/integrations/nphies/pre-auths/${row.id}/send`}
                    label={row.exchangeState === 'failed' ? 'Send again' : 'Send'}
                    doneTitle="Pre-authorisation answered"
                    module="nphies"
                    permission="nphies.preauth.send"
                    invalidate={[['pre-auth-tab'], ['pre-auth-counts'], ['nphies-status', 'pre_authorization', row.id]]}
                    beforeSend={row.admissionId ? (controls) => <PreAuthSendCheck preAuthId={row.id} {...controls} /> : undefined}
                  />
                )}
                {row.status === 'requested' && row.exchangeState === 'sent' && nphiesActionsFor(row, nphies) && can('nphies.status') && (
                  <Button size="sm" variant="outline" loading={act.isPending && act.variables?.id === row.id && act.variables.action === 'status-check'} onClick={() => act.mutate({ id: row.id, action: 'status-check' })}>
                    Check status
                  </Button>
                )}
                {['requested', 'approved', 'partially_approved'].includes(row.status) && nphiesActionsFor(row, nphies) && can('nphies.cancel') && (
                  <Button size="sm" variant="ghost" loading={act.isPending && act.variables?.id === row.id && act.variables.action === 'cancel'} onClick={() => act.mutate({ id: row.id, action: 'cancel' })}>
                    Cancel
                  </Button>
                )}
                {['approved', 'partially_approved'].includes(row.status) && can('invoice.create') && can('preauth.manage') && (
                  <Button size="sm" variant="outline" loading={bill.isPending && bill.variables === row.id} onClick={() => bill.mutate(row.id)}>
                    Bill approved services
                  </Button>
                )}
                {isDraft(row) && can('preauth.manage') && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => navigate(withReturnTo(`/insurance/pre-auths/${row.id}/edit`, returnTo))}>
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" loading={discard.isPending && discard.variables?.includes(row.id)} onClick={() => setDiscarding(row.id)}>
                      Discard
                    </Button>
                  </>
                )}
                {row.status === 'requested' && row.exchangeState !== 'queued' && can('preauth.manage') && (
                  <Button size="sm" variant="ghost" onClick={() => navigate(withReturnTo(`/insurance/pre-auths/${row.id}/respond`, returnTo))}>
                    Record answer
                  </Button>
                )}
                {(tab === 'rejected' || tab === 'failed') && can('preauth.manage') && (
                  <Button size="sm" variant="ghost" loading={act.isPending && act.variables?.id === row.id && act.variables.action === 'archive'} onClick={() => act.mutate({ id: row.id, action: 'archive' })}>
                    Archive
                  </Button>
                )}
              </span>
            ),
          },
        ]}
      />

      {viewing && <PreAuthDetailDialog id={viewing} onClose={() => setViewing(null)} />}
      {discarding && (
        <ConfirmDialog
          title={tr('billing', 'discard_preauth_draft_title')}
          description={tr('billing', 'discard_preauth_draft_body')}
          destructive
          confirmLabel={tr('billing', 'discard_draft')}
          onConfirm={() => discard.mutateAsync([discarding])}
          onClose={() => setDiscarding(null)}
        />
      )}
      {discardingSelected && (
        <ConfirmDialog
          title={tr('billing', 'discard_selected_preauths_title', { count: selected.length })}
          description={tr('billing', 'discard_selected_preauths_body')}
          destructive
          confirmLabel={tr('billing', 'discard_selected_preauths')}
          disabled={selected.length === 0}
          onConfirm={() => discard.mutateAsync(selected)}
          onClose={() => setDiscardingSelected(false)}
        />
      )}
    </>
  );
}
